import React, { useEffect, useRef } from 'react'; // Import useRef
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useQuery } from '@tanstack/react-query';
import { fetchSystemSettings } from '@/lib/api';
import { toast } from 'sonner'; // Import toast

const Success = () => {
  const { customerInfo, sendOrderToWhatsApp, clearCart, cartItems } = useCart();
  const navigate = useNavigate();
  const orderSentRef = useRef(false); // Ref to track if order sending was initiated

  // Fetch system settings
  const { data: settings, isLoading: loadingSettings, isError: errorLoadingSettings } = useQuery({
    queryKey: ['system-settings'],
    queryFn: fetchSystemSettings,
  });

  useEffect(() => {
    // Guard: If crucial info is missing on mount, redirect immediately.
    // This check prevents issues if the user directly navigates here without a cart.
    if (!customerInfo || !cartItems || cartItems.length === 0) {
      console.log("Redirecting from Success (Initial Mount/Guard): Missing customerInfo or cartItems empty");
      navigate('/catalog');
      return;
    }

    // Don't proceed if settings are loading or failed, or if order was already sent/attempted
    if (loadingSettings || errorLoadingSettings || orderSentRef.current) {
         if (errorLoadingSettings && !loadingSettings && !orderSentRef.current) { // Only show error if not already sent
            console.error("System settings could not be loaded. Cannot send order.");
            toast.error("Erro ao carregar configurações da loja. Não foi possível enviar o pedido automaticamente.");
            orderSentRef.current = true; // Mark as attempted to prevent retry loops
         }
        return;
    }

    // Ensure settings are loaded and whatsapp number exists
    if (settings && settings.order_whatsapp) {
        // Mark that we are attempting to send *before* the async call
        orderSentRef.current = true;

        const whatsappNumber = settings.order_whatsapp;
        console.log(`Attempting to send order to WhatsApp: ${whatsappNumber}`);

        // Call the function from the context
        sendOrderToWhatsApp(whatsappNumber)
          .then(() => {
             console.log("Order processing and WhatsApp initiation successful from Success page.");
             // clearCart() is handled within sendOrderToWhatsApp on its success
             // No need to call clearCart here.
          })
          .catch((error) => {
            // This catch block might be redundant if sendOrderToWhatsApp handles its own errors well,
            // but it's safe to have for unexpected issues.
            console.error('Error during sendOrderToWhatsApp execution from Success page:', error);
            // Do NOT clear cart here if the process failed. The user might need the cart state.
            toast.error("Falha ao finalizar o pedido. Verifique sua conexão ou contate o suporte.");
          });

    } else if (settings && !settings.order_whatsapp && !orderSentRef.current) {
         // Settings loaded, but no WhatsApp number configured
         console.error("WhatsApp number not configured in settings. Cannot auto-send.");
         toast.warn("Pedido registrado, mas não pudemos enviar para o WhatsApp automaticamente (não configurado).");
         orderSentRef.current = true; // Mark as attempted
         // We probably should NOT clear the cart here either. Maybe show the success page anyway?
         // Or redirect elsewhere? For now, let the success page render.
         // clearCart(); // Avoid clearing cart
    }

  // Dependencies: Trigger only when settings load/change.
  // Keep customerInfo/cartItems to trigger the initial guard check if they somehow change.
  // navigate is needed. sendOrderToWhatsApp is technically stable if defined via useCallback, but including it is safer.
  }, [customerInfo, cartItems, settings, loadingSettings, errorLoadingSettings, navigate, sendOrderToWhatsApp]);

   // --- Loading State ---
   // Show loading only while settings are initially loading.
   if (loadingSettings && !settings) {
     return (
         <div className="flex justify-center items-center h-screen"> {/* Full screen load */}
           <Loader2 className="h-12 w-12 animate-spin text-navy-600" />
           <p className="ml-4 text-lg">Finalizando seu pedido...</p>
         </div>
     );
   }

   // --- Error State / Post-Clear Redirect ---
   // If after loading, customerInfo is gone (likely due to clearCart completing),
   // redirect to catalog instead of showing the success message with no data.
   if (!customerInfo) {
       console.log("Redirecting after load because customerInfo is null (likely cart cleared)");
        // Navigate away silently, user should have seen toasts/whatsapp.
        navigate('/catalog');
        return null; // Render nothing while navigating
   }

  // --- Render Success Message ---
  // If we reach here, settings are loaded (or failed gracefully),
  // the order sending process was initiated, and customerInfo still exists.
  return (
    <main className="py-12 px-4 container mx-auto flex items-center justify-center" style={{ minHeight: 'calc(100vh - 160px)' }}>
      <div className="max-w-lg text-center bg-white p-8 rounded-lg shadow-md">
        <div className="flex justify-center mb-6">
          <CheckCircle2 className="h-20 w-20 text-green-500" />
        </div>

        <h1 className="text-2xl font-bold mb-3">Pedido finalizado!</h1>

        <p className="text-gray-600 mb-6">
          Obrigado, <span className="font-semibold">{customerInfo.name}</span>!
          Seu pedido foi registrado com sucesso. {
           settings?.order_whatsapp
             ? 'Estamos enviando uma cópia para nosso WhatsApp para processamento.'
             : 'Não foi possível enviar para o WhatsApp automaticamente (contate o suporte se necessário).'
          }
          {customerInfo.paymentInfo?.method === 'pix' &&
            ' Assim que confirmarmos seu pagamento PIX, iniciaremos a separação.'
          }
           {customerInfo.paymentInfo?.method === 'cash' &&
            ' O pagamento será realizado no momento da entrega.'
          }
          {(customerInfo.paymentInfo?.method === 'credit_card_machine' || customerInfo.paymentInfo?.method === 'debit_card_machine') &&
            ' O pagamento será realizado via maquininha no momento da entrega.'
          }
        </p>

        <div className="space-y-4">
          <Link to="/catalog">
            <Button className="w-full" size="lg">Continuar Comprando</Button>
          </Link>
        </div>
      </div>
    </main>
  );
};

export default Success;
